// Adapted from eval/dashboard/internal_web/src/components/session/DifferentialParts.tsx (ATM-57): PanelTabs, generalised past two views. Keep in step until ATM-60 shares it.
/**
 * A row of tabs, as the WAI-ARIA tabs pattern — the eval dashboard's panel tabs
 * (`DifferentialParts.PanelTabs`), generalised past two views.
 *
 * The arrow keys move between tabs (wrapping), Home and End jump to the ends, and only
 * the selected tab is in the tab order. The caller renders the tabpanel with
 * `id={tabPanelId(id)}` and `aria-labelledby={tabId(id, value)}`.
 */

import { useRef, type KeyboardEvent } from "react";

import { cn } from "../../lib/cn";
import { tabId, tabPanelId } from "./tabIds";

export type TabItem<T extends string> = { id: T; label: string };

type Props<T extends string> = {
  /** A stable id prefix, from `useId`. */
  id: string;
  /** What the tabs choose between, for screen readers ("Report view"). */
  label: string;
  items: readonly TabItem<T>[];
  value: T;
  onSelect: (value: T) => void;
  className?: string;
};

/** The tabs themselves: underlined when selected, faint otherwise. */
export default function Tabs<T extends string>({
  id,
  label,
  items,
  value,
  onSelect,
  className,
}: Props<T>) {
  const refs = useRef<Partial<Record<string, HTMLButtonElement | null>>>({});
  // A value outside the items must still leave one tab reachable by keyboard.
  const focusable = items.some((item) => item.id === value) ? value : items[0]?.id;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = items.findIndex((item) => item.id === value);
    const last = items.length - 1;
    const target =
      event.key === "ArrowRight"
        ? at === last ? 0 : at + 1
        : event.key === "ArrowLeft"
          ? at <= 0 ? last : at - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (target === null || items[target] === undefined) return;
    event.preventDefault();
    const next = items[target].id;
    onSelect(next);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn("flex gap-4", className)}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            ref={(element) => {
              refs.current[item.id] = element;
            }}
            type="button"
            role="tab"
            id={tabId(id, item.id)}
            aria-selected={selected}
            aria-controls={tabPanelId(id)}
            tabIndex={item.id === focusable ? 0 : -1}
            onClick={() => onSelect(item.id)}
            className={cn(
              "whitespace-nowrap border-b-2 pb-0.5 text-panel transition-colors",
              selected ? "border-ink text-ink" : "border-transparent text-ink-faint hover:text-ink-3",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
